import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	createBulkOrderRequest,
	createOrderRequest,
	getOrderRequestById,
	getOrderShipments,
	getRecentOrderRequests,
	getRequests,
	getRequestsProducts,
	updateBulkOrderRequest,
	updateOrderRequest,
} from '@/services/requests';
import type { BenefitVerification } from '@/types/benefitsVerification';
import type { BulkOrderRequest } from '@/types/bulk-order';
import type { UpdateBVProductPayload } from '@/types/product';
import type { CreateOrderRequest } from '@/types/requests';
import type { FilterType } from '@/types/table';

export const useGetRequests = ({
	page = 1,
	limit = 15,
	enabled = true,
	columnsFilters = '[]',
	search = '',
	status = '',
	globalFilters = '[]',
	sort = '',
}: FilterType) => {
	return useQuery({
		queryKey: [
			'requests',
			page,
			limit,
			columnsFilters,
			search,
			status,
			globalFilters,
			sort,
		],
		queryFn: () =>
			getRequests(
				page,
				limit,
				columnsFilters,
				search,
				status as string | number,
				globalFilters as string,
				sort as string,
			),
		enabled,
		keepPreviousData: false,
	});
};

export const useGetRequestProducts = ({
	page = 1,
	limit = 15,
	enabled = true,
	globalFilters = '[]',
}: FilterType) => {
	return useQuery({
		queryKey: ['requests-products', page, limit, globalFilters],
		queryFn: () => getRequestsProducts(page, limit, globalFilters as string),
		enabled,
		keepPreviousData: false,
	});
};

/**
 * Hook to get single order request by ID
 */
export const useGetOrderById = (orderId: string | null, enabled: boolean) => {
	return useQuery({
		queryKey: ['order', orderId],
		queryFn: () => getOrderRequestById(orderId as string),
		enabled: enabled && !!orderId,
		keepPreviousData: false,
	});
};

export const useGetOrderByIdMutation = (onSuccess?: (data: any) => void) => {
	return useMutation({
		mutationFn: (orderId: string) => getOrderRequestById(orderId),
		onSuccess: (data) => {
			onSuccess?.(data);
		},
		onError: (error: unknown) => {
			toast.error(
				(error as any)?.response?.data?.error?.message?.message ||
					'Failed to fetch order details. Please try again.',
			);
		},
	});
};

/**
 * Hook to get order with shipment details (tracking information)
 */
export const useGetOrderShipments = (
	orderId: string | null,
	enabled: boolean,
) => {
	return useQuery({
		queryKey: ['order-shipments', orderId],
		queryFn: () => getOrderShipments(orderId as string),
		enabled: enabled && !!orderId,
		keepPreviousData: false,
	});
};

/**
 * Hook to create single order request
 */
export const useCreateOrderRequest = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: {
			orderRequest: CreateOrderRequest;
			bvPayload: {
				bvId: string;
				payload: Partial<BenefitVerification>;
			};
			bvProductPayload: {
				priorAuthId: string;
				payload: UpdateBVProductPayload;
			};
		}) => createOrderRequest(payload),
		onSuccess: () => {
			queryClient.invalidateQueries(['requests']);
			if (onSuccess) onSuccess();
		},
	});
};

/**
 * Hook to update single order request
 */
export const useUpdateOrderRequest = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ orderId, payload }: { orderId: string; payload: any }) =>
			updateOrderRequest(orderId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries(['requests']);
			if (onSuccess) onSuccess();
		},
	});
};

/**
 * Hook to create bulk order request
 * Sends ONE request to backend which creates multiple orders
 */
export const useCreateBulkOrderRequest = (onSuccess?: (data: any) => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: BulkOrderRequest) => createBulkOrderRequest(payload),
		onSuccess: (data) => {
			queryClient.invalidateQueries(['requests']);
			if (onSuccess) onSuccess(data);
		},
	});
};

/**
 * Hook to update bulk order request
 */
export const useUpdateBulkOrderRequest = (onSuccess?: (data: any) => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: any) => updateBulkOrderRequest(payload),
		onSuccess: (data) => {
			queryClient.invalidateQueries(['requests']);
			if (onSuccess) onSuccess(data);
		},
	});
};

export const useGetRecentRequests = ({
	page = 1,
	limit = 50,
	enabled = true,
	clinics = [],
	port = '',
}: FilterType & { clinics: string[]; port: string }) => {
	return useQuery({
		queryKey: [
			'recent-requests',
			page,
			limit,
			clinics,
			port,
		],
		queryFn: () =>
			getRecentOrderRequests(
				page,
				limit,
				clinics,
				port
			),
		enabled,
		keepPreviousData: false,
	});
};